import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions, googleProvider } from '../lib/firebase';

const AuthContext = createContext(null);
const syncCurrentUserProfile = httpsCallable(functions, 'syncCurrentUserProfile');
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const shouldSyncProfile =
  !isLocalDev || process.env.REACT_APP_ENABLE_REMOTE_PROFILE_SYNC === 'true';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      try {
        if (nextUser && shouldSyncProfile) {
          await syncCurrentUserProfile();
        }

        setUser(nextUser);
        setError(null);
      } catch (authError) {
        console.error('Failed to sync user profile:', authError);
        setUser(nextUser);
        setError(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (signInError) {
      console.error('Sign-in error:', signInError);
      setError('Failed to sign in');
      throw signInError;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      setUser(null);
    } catch (signOutError) {
      console.error('Sign-out error:', signOutError);
      setError('Failed to sign out');
      throw signOutError;
    }
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      signInWithGoogle,
      logout,
    }),
    [user, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
